import { useState, useMemo, useCallback } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CloseIcon from '@mui/icons-material/Close';
import { Link } from 'react-router-dom';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PublishIcon from '@mui/icons-material/Publish';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import { SeoTask, SeoTrackedKeyword } from '@/types/api/seo';
import { DataTable, PageHeader, FilterTabs, EmptyState, Column } from '@/components/shared';

type Props = {
  tasks: SeoTask[];
  keywords: SeoTrackedKeyword[];
  keywordPageMap: Map<string, string>;
  isLoading: boolean;
  onStatusChange: (taskId: string, status: SeoTask['status']) => Promise<void>;
  onBulkDelete: (ids: string[]) => Promise<void>;
  onApplyToWp: (task: SeoTask) => Promise<void>;
  wpConnected: boolean;
};

type StatusFilter = 'all' | 'todo' | 'in_progress' | 'done';

const typeColors: Record<SeoTask['taskType'], string> = {
  title_tag: '#1a73e8', meta_description: '#0D9D58', content: '#A142F4',
  schema: '#F4B400', internal_link: '#4285F4', technical: '#EA4335',
  backlink: '#054AD9', other: '#96979b',
};

const typeLabels: Record<SeoTask['taskType'], string> = {
  title_tag: 'Title Tag', meta_description: 'Meta Desc', content: 'Content',
  schema: 'Schema', internal_link: 'Internal Link', technical: 'Technical',
  backlink: 'Backlink', other: 'Other',
};

const priorityColors: Record<SeoTask['priority'], string> = {
  high: '#EA4335', medium: '#F4B400', low: '#96979b',
};

const statusLabels: Record<SeoTask['status'], string> = {
  todo: 'To Do', in_progress: 'In Progress', done: 'Done', dismissed: 'Dismissed',
};

const statusColors: Record<SeoTask['status'], string> = {
  todo: '#4285F4', in_progress: '#F4B400', done: '#0D9D58', dismissed: '#96979b',
};

const WP_APPLICABLE_TYPES: SeoTask['taskType'][] = ['title_tag', 'meta_description', 'content', 'schema'];

const SeoTaskListComponent = function ({
  tasks,
  keywords,
  keywordPageMap,
  isLoading,
  onStatusChange,
  onBulkDelete,
  onApplyToWp,
  wpConnected,
}: Props): JSX.Element {
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [updatingId, setUpdatingId] = useState<string | null>(null);
  const [applyingId, setApplyingId] = useState<string | null>(null);
  const [confirmDismissId, setConfirmDismissId] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [confirmBulkDelete, setConfirmBulkDelete] = useState(false);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);
  const [activeTask, setActiveTask] = useState<SeoTask | null>(null);

  const keywordMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const kw of keywords) map.set(kw.id, kw.keyword);
    return map;
  }, [keywords]);

  const filteredTasks = useMemo(() => {
    if (statusFilter === 'all') return tasks;
    return tasks.filter((t) => t.status === statusFilter);
  }, [tasks, statusFilter]);

  const handleStatusClick = useCallback(async (taskId: string, status: SeoTask['status']) => {
    setUpdatingId(taskId);
    await onStatusChange(taskId, status);
    setUpdatingId(null);
  }, [onStatusChange]);

  const handleConfirmBulkDelete = useCallback(async () => {
    setIsBulkDeleting(true);
    await onBulkDelete(Array.from(selectedIds));
    setSelectedIds(new Set());
    setConfirmBulkDelete(false);
    setIsBulkDeleting(false);
  }, [selectedIds, onBulkDelete]);

  const handleConfirmDismiss = useCallback(async () => {
    if (!confirmDismissId) return;
    await handleStatusClick(confirmDismissId, 'dismissed');
    setConfirmDismissId(null);
  }, [confirmDismissId, handleStatusClick]);

  const handleApplyClick = useCallback(async (task: SeoTask) => {
    setApplyingId(task.id);
    await onApplyToWp(task);
    setApplyingId(null);
  }, [onApplyToWp]);

  const filterTabs = [
    { key: 'all', label: 'All', count: tasks.length },
    { key: 'todo', label: 'To Do', count: tasks.filter((t) => t.status === 'todo').length },
    { key: 'in_progress', label: 'In Progress', count: tasks.filter((t) => t.status === 'in_progress').length },
    { key: 'done', label: 'Done', count: tasks.filter((t) => t.status === 'done').length },
  ];

  const columns: Column<SeoTask>[] = [
    {
      id: 'task',
      label: 'Task',
      render: (task) => (
        <Box>
          <Typography variant="body2" component="div" fontWeight={600} sx={{ fontSize: 13 }}>
            {task.title}
          </Typography>
          {task.suggestedValue && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 10 }}>
              {task.suggestedValue.slice(0, 80)}...
            </Typography>
          )}
        </Box>
      ),
    },
    {
      id: 'keyword',
      label: 'Keyword',
      render: (task) => {
        const kw = task.keywordId ? keywordMap.get(task.keywordId) : null;
        if (!kw || !task.keywordId) {
          return <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11 }}>—</Typography>;
        }
        return (
          <Link
            to={`/seox/projects/${task.projectId}/keywords/${task.keywordId}`}
            style={{ fontSize: 11, color: '#054AD9', textDecoration: 'none', fontWeight: 500 }}
          >
            {kw}
          </Link>
        );
      },
    },
    {
      id: 'page',
      label: 'Page',
      render: (task) => {
        const url = task.keywordId ? keywordPageMap.get(task.keywordId) : null;
        if (!url) return <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11 }}>—</Typography>;
        const path = url.replace(/^https?:\/\/[^/]+/, '') || '/';
        return (
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            title={url}
            style={{
              fontSize: 11,
              maxWidth: 200,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              display: 'block',
              color: '#054AD9',
              textDecoration: 'none',
            }}
          >
            {path}
          </a>
        );
      },
    },
    {
      id: 'type',
      label: 'Type',
      align: 'center',
      render: (task) => (
        <Chip
          label={typeLabels[task.taskType] ?? task.taskType}
          size="small"
          sx={{ fontSize: 10, height: 20, color: '#fff', backgroundColor: typeColors[task.taskType] ?? '#96979b' }}
        />
      ),
    },
    {
      id: 'priority',
      label: 'Priority',
      align: 'center',
      render: (task) => (
        <Chip
          label={task.priority}
          size="small"
          sx={{ fontSize: 10, height: 18, fontWeight: 700, textTransform: 'uppercase', color: '#fff', backgroundColor: priorityColors[task.priority] }}
        />
      ),
    },
    {
      id: 'status',
      label: 'Status',
      align: 'center',
      render: (task) => (
        <Chip
          label={statusLabels[task.status]}
          size="small"
          sx={{ fontSize: 10, height: 20, color: '#fff', backgroundColor: statusColors[task.status] }}
        />
      ),
    },
    {
      id: 'actions',
      label: 'Actions',
      align: 'center',
      render: (task) => {
        const isUpdating = updatingId === task.id;
        const isApplying = applyingId === task.id;
        const canApplyToWp = wpConnected && !task.appliedToWp && task.status !== 'done' && task.suggestedValue && WP_APPLICABLE_TYPES.includes(task.taskType);

        return (
          <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
            {task.status === 'todo' && (
              <Button size="small" startIcon={isUpdating ? <CircularProgress size={12} /> : <PlayArrowIcon sx={{ fontSize: 14 }} />}
                onClick={() => handleStatusClick(task.id, 'in_progress')} disabled={isUpdating}
                sx={{ fontSize: 10, textTransform: 'none', minWidth: 0, px: 1 }}>
                Start
              </Button>
            )}
            {(task.status === 'todo' || task.status === 'in_progress') && (
              <Button size="small" startIcon={isUpdating ? <CircularProgress size={12} /> : <CheckCircleOutlineIcon sx={{ fontSize: 14 }} />}
                onClick={() => handleStatusClick(task.id, 'done')} disabled={isUpdating} color="success"
                sx={{ fontSize: 10, textTransform: 'none', minWidth: 0, px: 1 }}>
                Done
              </Button>
            )}
            {canApplyToWp && (
              <Button size="small" variant="outlined" startIcon={isApplying ? <CircularProgress size={12} /> : <PublishIcon sx={{ fontSize: 14 }} />}
                onClick={() => handleApplyClick(task)} disabled={isApplying}
                sx={{ fontSize: 10, textTransform: 'none', minWidth: 0, px: 1, color: '#21759B', borderColor: '#21759B' }}>
                WP
              </Button>
            )}
            {task.appliedToWp && (
              <Chip label="Applied" size="small" color="success" sx={{ fontSize: 9, height: 18 }} />
            )}
            {task.status !== 'done' && task.status !== 'dismissed' && (
              <Button size="small" startIcon={<CloseIcon sx={{ fontSize: 12 }} />}
                onClick={() => setConfirmDismissId(task.id)} disabled={isUpdating}
                sx={{ fontSize: 10, textTransform: 'none', minWidth: 0, px: 0.5, color: 'text.secondary' }}>
              </Button>
            )}
          </Box>
        );
      },
    },
  ];

  return (
    <Box sx={{}}>
      <PageHeader title="Tasks" />

      <Box sx={{ mb: 2 }}>
        <FilterTabs
          tabs={filterTabs}
          activeKey={statusFilter}
          onChange={(key) => setStatusFilter(key as StatusFilter)}
        />
      </Box>

      <DataTable
        columns={columns}
        data={filteredTasks}
        isLoading={isLoading}
        rowKey={(t) => t.id}
        searchPlaceholder="Search tasks..."
        searchFields={(t) => `${t.title} ${t.description ?? ''} ${t.keywordId ? keywordMap.get(t.keywordId) ?? '' : ''}`}
        emptyMessage="No tasks yet. Generate AI suggestions in the Ranking Playbook, then click 'Add Task'."
        emptyIcon={AssignmentOutlinedIcon}
        onRowClick={(task) => setActiveTask(task)}
        selectable
        selectedIds={selectedIds}
        onSelectionChange={setSelectedIds}
        selectionActions={
          <Button
            size="small"
            variant="outlined"
            color="error"
            startIcon={<DeleteOutlineIcon />}
            onClick={() => setConfirmBulkDelete(true)}
            sx={{ textTransform: 'none', fontSize: 12 }}
          >
            Delete {selectedIds.size} task{selectedIds.size !== 1 ? 's' : ''}
          </Button>
        }
      />

      <Dialog open={confirmBulkDelete} onClose={() => setConfirmBulkDelete(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: 16, fontWeight: 700 }}>Delete {selectedIds.size} task{selectedIds.size !== 1 ? 's' : ''}?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            Are you sure you want to delete the selected tasks? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmBulkDelete(false)} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmBulkDelete}
            disabled={isBulkDeleting}
            sx={{ textTransform: 'none' }}
          >
            {isBulkDeleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!confirmDismissId} onClose={() => setConfirmDismissId(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: 16, fontWeight: 700 }}>Delete task?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            Are you sure you want to delete this task? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmDismissId(null)} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmDismiss}
            sx={{ textTransform: 'none' }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Task Detail Drawer */}
      {/* Backdrop */}
      {activeTask && (
        <Box
          onClick={() => setActiveTask(null)}
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.15)',
            zIndex: 10,
          }}
        />
      )}
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          right: 0,
          width: 420,
          height: '100vh',
          backgroundColor: '#fff',
          boxShadow: '-4px 0 24px rgba(0,0,0,0.1)',
          zIndex: 11,
          transform: activeTask ? 'translateX(0)' : 'translateX(440px)',
          transition: 'transform 300ms cubic-bezier(0.2, 0.8, 0.4, 1)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {activeTask && (() => {
          const kw = activeTask.keywordId ? keywordMap.get(activeTask.keywordId) : null;
          const pageUrl = activeTask.keywordId ? keywordPageMap.get(activeTask.keywordId) : null;
          const pagePath = pageUrl ? pageUrl.replace(/^https?:\/\/[^/]+/, '') || '/' : null;
          const canApply = wpConnected && !activeTask.appliedToWp && activeTask.status !== 'done' && activeTask.suggestedValue && WP_APPLICABLE_TYPES.includes(activeTask.taskType);

          return (
            <>
              {/* Header */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 3, py: 2, borderBottom: '1px solid #F4F4F5' }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <Chip
                      label={typeLabels[activeTask.taskType] ?? activeTask.taskType}
                      size="small"
                      sx={{ fontSize: 10, height: 20, color: '#fff', backgroundColor: typeColors[activeTask.taskType] ?? '#96979b' }}
                    />
                    <Chip
                      label={activeTask.priority}
                      size="small"
                      sx={{ fontSize: 10, height: 18, fontWeight: 700, textTransform: 'uppercase', color: '#fff', backgroundColor: priorityColors[activeTask.priority] }}
                    />
                    <Chip
                      label={statusLabels[activeTask.status]}
                      size="small"
                      sx={{ fontSize: 10, height: 20, color: '#fff', backgroundColor: statusColors[activeTask.status] }}
                    />
                  </Box>
                  <Typography sx={{ fontSize: 15, fontWeight: 700, color: '#09090B', lineHeight: 1.3 }}>
                    {activeTask.title}
                  </Typography>
                </Box>
                <Button
                  onClick={() => setActiveTask(null)}
                  sx={{ minWidth: 0, p: 0.5, color: '#71717A' }}
                >
                  <CloseIcon sx={{ fontSize: 20 }} />
                </Button>
              </Box>

              {/* Scrollable body */}
              <Box sx={{ flex: 1, overflowY: 'auto', px: 3, py: 2 }}>
                {/* Keyword & Page */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 3 }}>
                  {kw && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography sx={{ fontSize: 12, color: '#71717A', width: 60, flexShrink: 0 }}>Keyword</Typography>
                      <Link
                        to={`/seox/projects/${activeTask.projectId}/keywords/${activeTask.keywordId}`}
                        style={{ fontSize: 13, color: '#054AD9', textDecoration: 'none', fontWeight: 500 }}
                        onClick={() => setActiveTask(null)}
                      >
                        {kw}
                      </Link>
                    </Box>
                  )}
                  {pagePath && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography sx={{ fontSize: 12, color: '#71717A', width: 60, flexShrink: 0 }}>Page</Typography>
                      <a
                        href={pageUrl ?? ''}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ fontSize: 13, color: '#054AD9', textDecoration: 'none' }}
                      >
                        {pagePath}
                      </a>
                    </Box>
                  )}
                </Box>

                {/* Description / Why this matters */}
                {activeTask.description && (
                  <Box sx={{ mb: 3 }}>
                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#71717A', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 1 }}>
                      Why this matters
                    </Typography>
                    <Typography sx={{ fontSize: 13, color: '#4A4B4E', lineHeight: 1.6 }}>
                      {activeTask.description}
                    </Typography>
                  </Box>
                )}

                {/* Suggested Change */}
                {activeTask.suggestedValue && (
                  <Box sx={{ mb: 3 }}>
                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#71717A', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 1 }}>
                      Suggested change
                    </Typography>
                    <Box
                      sx={{
                        backgroundColor: '#F0FDF4',
                        border: '1px solid #BBF7D0',
                        borderRadius: '8px',
                        p: 2,
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                        <ArrowForwardIcon sx={{ fontSize: 14, color: '#16A34A' }} />
                        <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#16A34A', textTransform: 'uppercase' }}>
                          {typeLabels[activeTask.taskType] ?? activeTask.taskType}
                        </Typography>
                      </Box>
                      <Typography
                        sx={{
                          fontSize: 13,
                          color: '#09090B',
                          lineHeight: 1.6,
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                          fontFamily: activeTask.taskType === 'schema' ? 'monospace' : 'inherit',
                        }}
                      >
                        {activeTask.suggestedValue}
                      </Typography>
                    </Box>
                  </Box>
                )}

                {/* Applied status */}
                {activeTask.appliedToWp && (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      p: 1.5,
                      borderRadius: '8px',
                      backgroundColor: '#F0FDF4',
                      border: '1px solid #BBF7D0',
                      mb: 3,
                    }}
                  >
                    <CheckCircleOutlineIcon sx={{ fontSize: 16, color: '#16A34A' }} />
                    <Typography sx={{ fontSize: 12, color: '#16A34A', fontWeight: 500 }}>
                      Applied to WordPress{activeTask.appliedAt && ` on ${new Date(activeTask.appliedAt).toLocaleDateString()}`}
                    </Typography>
                  </Box>
                )}
              </Box>

              {/* Footer actions */}
              {activeTask.status !== 'done' && activeTask.status !== 'dismissed' && (
                <Box
                  sx={{
                    display: 'flex',
                    gap: 1,
                    px: 3,
                    py: 2,
                    borderTop: '1px solid #F4F4F5',
                  }}
                >
                  {canApply && (
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<PublishIcon sx={{ fontSize: 16 }} />}
                      onClick={async () => {
                        await onApplyToWp(activeTask);
                        setActiveTask(null);
                      }}
                      sx={{ backgroundColor: '#21759B', '&:hover': { backgroundColor: '#1a5f7c' }, textTransform: 'none', fontSize: 12, flex: 1 }}
                    >
                      Apply to WordPress
                    </Button>
                  )}
                  <Button
                    variant="contained"
                    size="small"
                    color="success"
                    startIcon={<CheckCircleOutlineIcon sx={{ fontSize: 16 }} />}
                    onClick={async () => {
                      await handleStatusClick(activeTask.id, 'done');
                      setActiveTask(null);
                    }}
                    sx={{ textTransform: 'none', fontSize: 12, flex: canApply ? undefined : 1 }}
                  >
                    Mark Done
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    color="error"
                    onClick={() => {
                      setConfirmDismissId(activeTask.id);
                      setActiveTask(null);
                    }}
                    sx={{ textTransform: 'none', fontSize: 12 }}
                  >
                    Delete
                  </Button>
                </Box>
              )}
            </>
          );
        })()}
      </Box>
    </Box>
  );
};

export default SeoTaskListComponent;
